import React from "react";
import styled from "styled-components";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import TextLink from "../library/TextLink";
import { TextInputField } from "../library/Form";
import SubmitButton from "../library/Form/SubmitButton";
import EmployerUser from "../../apiResources/EmployerUser";
import * as Sentry from "@sentry/react";
import SettingLabel from "./SettingLabel";
import Skeleton from "../../components/library/Skeleton";

const TwitterSettingOption = (props: Props) => {
  const {
    user,
    isEditing = false,
    disabled = false,
    isLoading = false,
    onEdit,
    onDone,
    onUserChange,
  } = props;

  const save = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    try {
      user.assignAttributes(values);
      const success = await user.save();
      if (success) {
        onUserChange?.(user);
        onDone();
      } else {
        formik.setErrors({
          twitterUsername: user.errors.twitterUsername?.message,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const edit = () => {
    if (!disabled) {
      onEdit();
    }
  };

  const value = user?.twitterUsername;
  const showNotSpecified = !isLoading && !value;
  const showValue = !isLoading && Boolean(value);

  if (!isEditing) {
    return (
      <section>
        <Header>
          <SettingLabel>X username</SettingLabel>
          <TextLink disabled={disabled} onClick={edit}>
            Edit
          </TextLink>
        </Header>

        {isLoading && <Skeleton height="22" />}
        {showNotSpecified && <div>Not specified</div>}
        {showValue && <div>@{value}</div>}
      </section>
    );
  }

  const initialValues: FormValues = {
    twitterUsername: user.twitterUsername ?? "",
  };

  const validation = Yup.object({
    twitterUsername: Yup.string().required(
      "Please enter your X username"
    ),
  });

  return (
    <section>
      <Formik
        initialValues={initialValues}
        onSubmit={save}
        validationSchema={validation}
      >
        <Form autoComplete="off">
          <Header>
            <SettingLabel>X username</SettingLabel>
            <TextLink onClick={onDone}>Cancel</TextLink>
          </Header>
          <HintText>
            You only need to enter your username, don&apos;t include the @!
          </HintText>
          <TextInputField
            label="X username"
            name="twitterUsername"
            prefixDisplay="@"
            hiddenLabel
          />
          <SubmitButton />
        </Form>
      </Formik>
    </section>
  );
};

type Props = {
  user: EmployerUser;
  isEditing?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onEdit: () => void;
  onDone: () => void;
  onUserChange?: (user: EmployerUser) => void;
};

type FormValues = {
  twitterUsername: string;
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const HintText = styled.div`
  color: ${({ theme }) => theme.colors.gray500};
  margin-bottom: 1rem;
`;

export default TwitterSettingOption;
